import React, { SyntheticEvent } from 'react'

export interface NumberFormatValues {
  floatValue: string | number | undefined
  formattedValue: string
  value: string
}

export type Timeout = ReturnType<typeof setTimeout>

export enum SourceType {
  event = 'event',
  props = 'prop',
}

export interface SourceInfo {
  event?: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>
  source: SourceType
}

export type InputAttributes = Omit<
  React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
  'defaultValue' | 'value' | 'children'
>

type NumberFormatProps<Props, BaseType = InputAttributes> = Props &
  Omit<InputAttributes, keyof BaseType> &
  Omit<BaseType, keyof Props | 'ref'>

export type FormatInputValueFunction = (inputValue: string) => string

export type ChangeMeta = {
  from: {
    start: number
    end: number
  }
  to: {
    start: number
    end: number
  }
  lastValue: string
}

export type RemoveFormattingFunction = (
  inputValue: string,
  changeMeta?: ChangeMeta,
) => string

export type OnValueChange = (
  values: NumberFormatValues,
  sourceInfo: SourceInfo,
) => void

type NumberFormatBase = {
  type?: 'text' | 'tel' | 'password'
  displayType?: 'input' | 'text'
  renderText?: (
    formattedValue: string,
    otherProps: Partial<NumberFormatBase>,
  ) => React.ReactNode
  format: FormatInputValueFunction
  removeFormatting?: RemoveFormattingFunction
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getInputRef?:
    | ((el: HTMLInputElement | HTMLTextAreaElement) => void)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | React.Ref<any>
  value?: number | string | null
  defaultValue?: number | string | null
  valueIsNumericString?: boolean
  onValueChange?: OnValueChange
  isAllowed?: (values: NumberFormatValues) => boolean
  onKeyDown?: InputAttributes['onKeyDown']
  onMouseUp?: InputAttributes['onMouseUp']
  onChange?: InputAttributes['onChange']
  onFocus?: InputAttributes['onFocus']
  onBlur?: InputAttributes['onBlur']
  onPaste?: InputAttributes['onPaste']
  decimalSeparator?: string
  getCaretBoundary?: (formattedValue: string) => boolean[]
  isValidInputCharacter?: (character: string) => boolean
}

export type InternalNumberFormatBase = Omit<
  NumberFormatBase,
  'format' | 'removeFormatting' | 'getCaretBoundary' | 'isValidInputCharacter'
>

export type NumericFormatProps<BaseType = InputAttributes> = NumberFormatProps<
  InternalNumberFormatBase & {
    thousandSeparator?: boolean | string
    decimalSeparator?: string
    staticSeparator?: boolean
    allowedDecimalSeparators?: Array<string>
    thousandsGroupStyle?: 'thousand' | 'lakh' | 'wan' | 'none'
    decimalScale?: number
    fixedDecimalScale?: boolean
    allowNegative?: boolean
    allowLeadingZeros?: boolean
    suffix?: string
    prefix?: string
  },
  BaseType
>

export type NumberFormatBaseProps<BaseType = InputAttributes> =
  NumberFormatProps<NumberFormatBase, BaseType>

export type PatternFormatProps<BaseType = InputAttributes> = NumberFormatProps<
  InternalNumberFormatBase & {
    format: string
    mask?: string | string[]
    allowEmptyFormatting?: boolean
    patternChar?: string
  },
  BaseType
>
